import { CommonModule } from '@angular/common';
import { Component, computed, inject, Input } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { LayoutService } from '@/layout/service/layout.service';
import { DrawerModule } from 'primeng/drawer';
import { SelectButtonModule } from 'primeng/selectbutton';
import { ButtonModule } from 'primeng/button';
import { RippleModule } from 'primeng/ripple';
import { TooltipModule } from 'primeng/tooltip';
import { OnInit } from '@angular/core';
import { ThemeRuntimeService } from '@/theme/theme-runtime.service';
import {
    BACKGROUND_SCHEMES,
    BRAND_PRIMARY_COLORS,
    EXPLORATION_PRIMARY_COLORS,
    LOGO_VARIANTS,
    LogoVariantOption,
    SIDEBAR_SCHEMES,
    SURFACES,
    THEME_PRESETS,
    ThemePresetOption
} from '@/theme/theme-catalog';
import { environment } from 'environments/environment';
/**
 * Configurador de tema (drawer del topbar).
 *
 * Es SOLO interfaz: toda la lógica de aplicar el tema vive en
 * ThemeRuntimeService, y el conjunto de opciones en theme-catalog.ts. Para
 * añadir un color o un esquema se toca el catálogo, no este archivo.
 *
 * `[simple]="true"` es el modo que usan las pantallas de `modules/auth/*`: ahí
 * no hay sidebar ni fondo de layout que configurar, así que solo se muestra
 * claro/oscuro, y el propio componente pinta su botón flotante.
 */
@Component({
    selector: 'app-configurator',
    standalone: true,
    imports: [CommonModule, FormsModule, SelectButtonModule, DrawerModule, RippleModule, ButtonModule, TooltipModule],
    template: `
        <button *ngIf="simple" class="layout-config-button config-link" type="button" (click)="toggleConfigSidebar()">
            <i class="pi pi-cog"></i>
        </button>

        <p-drawer
            [(visible)]="visible"
            (onHide)="layoutService.hideConfigSidebar()"
            position="right"
            [transitionOptions]="'.3s cubic-bezier(0, 0, 0.2, 1)'"
            styleClass="layout-config-sidebar w-96"
            header="Apariencia"
        >
            <div class="flex flex-col gap-6">
                @if (!simple && allowThemes) {
                    <!-- Presets ---------------------------------------------------->
                    <section class="flex flex-col gap-2">
                        <span class="text-lg font-semibold">Temas</span>
                        <span class="text-xs text-sym-gray-500">Combinaciones listas. Puedes ajustar cualquier detalle después.</span>
                        <div class="flex flex-col gap-2">
                            @for (preset of themePresets; track preset.id) {
                                <button type="button" (click)="selectPreset(preset)" [ngClass]="presetTileClass(preset.id === activePreset())">
                                    <span class="w-10 h-7 rounded shrink-0 border border-sym-border" [style.background]="preset.preview"></span>
                                    <span class="flex flex-col min-w-0 text-left">
                                        <span class="text-sm font-medium truncate">{{ preset.label }}</span>
                                        <span class="text-xs text-sym-gray-500 truncate">{{ preset.hint }}</span>
                                    </span>
                                    @if (preset.id === activePreset()) {
                                        <i class="pi pi-check ml-auto text-sm"></i>
                                    }
                                </button>
                            }
                        </div>
                    </section>
                }

                <!-- Esquema de color ------------------------------------------->
                <section class="flex flex-col gap-2">
                    <span class="text-lg font-semibold">Esquema de color</span>
                    <p-selectbutton [ngModel]="darkTheme()" (ngModelChange)="toggleDarkMode()" [options]="themeOptions" optionLabel="name" optionValue="value" [allowEmpty]="false" />
                </section>

                @if (allowThemes) {
                    <!-- Color primario --------------------------------------------->
                    <section class="flex flex-col gap-3">
                        <span class="text-lg font-semibold">Color primario</span>

                        <div class="flex flex-col gap-2">
                            <span class="text-xs uppercase tracking-widest text-sym-gray-500">Marca SyM70</span>
                            <div class="flex gap-2 flex-wrap">
                                @for (color of brandColors; track color.id) {
                                    <button type="button" [title]="color.label" (click)="selectPrimary(color.id)" [ngClass]="swatchClass(color.id === selectedPrimary())" [style.background-color]="color.swatch"></button>
                                }
                            </div>
                        </div>

                        <div class="flex flex-col gap-2">
                            <span class="text-xs uppercase tracking-widest text-sym-gray-500">Exploración</span>
                            <div class="flex gap-2 flex-wrap">
                                @for (color of explorationColors; track color.id) {
                                    <button
                                        type="button"
                                        [title]="color.label"
                                        (click)="selectPrimary(color.id)"
                                        [ngClass]="swatchClass(color.id === selectedPrimary())"
                                        [style.background-color]="color.swatch ?? 'var(--sym-ink)'"
                                    ></button>
                                }
                            </div>
                            <span class="text-xs text-sym-gray-500">Fuera del sistema de diseño. Cambia también los tokens de marca para que la app quede coherente.</span>
                        </div>
                    </section>

                    <!-- Superficie ------------------------------------------------->
                    <section class="flex flex-col gap-2">
                        <span class="text-lg font-semibold">Superficie</span>
                        <div class="flex gap-2 flex-wrap">
                            @for (surface of surfaces; track surface.id) {
                                <button
                                    type="button"
                                    [title]="surface.label"
                                    (click)="selectSurface(surface.id)"
                                    [ngClass]="swatchClass(surface.id === selectedSurface())"
                                    [style.background-color]="surface.palette['500']"
                                ></button>
                            }
                        </div>
                    </section>
                }

                @if (!simple && allowThemes) {
                    <!-- Sidebar ---------------------------------------------------->
                    <section class="flex flex-col gap-2">
                        <span class="text-lg font-semibold">Sidebar</span>
                        <div class="grid grid-cols-2 gap-2">
                            @for (scheme of sidebarSchemes; track scheme.id) {
                                <button type="button" (click)="selectSidebarScheme(scheme.id)" [ngClass]="tileClass(scheme.id === selectedSidebarScheme())">
                                    <span class="w-6 h-6 rounded shrink-0 border border-sym-border" [style.background]="scheme.preview"></span>
                                    <span class="truncate">{{ scheme.label }}</span>
                                </button>
                            }
                        </div>
                    </section>

                    <!-- Marca ------------------------------------------------------>
                    <section class="flex flex-col gap-2">
                        <span class="text-lg font-semibold">Marca</span>
                        <div class="grid grid-cols-3 gap-2">
                            @for (variant of logoVariants; track variant.id) {
                                <button type="button" [title]="variant.label" (click)="selectLogoVariant(variant.id)" [ngClass]="markTileClass(variant.id === selectedLogoVariant())">
                                    <img [src]="variantPreview(variant)" [alt]="variant.label" class="h-5 w-auto max-w-full object-contain" />
                                    <span class="text-xs truncate">{{ variant.label }}</span>
                                </button>
                            }
                        </div>
                        <span class="text-xs text-sym-gray-500">Cómo se muestra la marca en el sidebar.</span>
                    </section>

                    <!-- Fondo ------------------------------------------------------>
                    <section class="flex flex-col gap-2">
                        <span class="text-lg font-semibold">Fondo</span>
                        <div class="grid grid-cols-2 gap-2">
                            @for (scheme of backgroundSchemes; track scheme.id) {
                                <button type="button" (click)="selectBackgroundScheme(scheme.id)" [ngClass]="tileClass(scheme.id === selectedBackgroundScheme())">
                                    <span class="w-6 h-6 rounded shrink-0 border border-sym-border" [style.background]="scheme.preview"></span>
                                    <span class="truncate">{{ scheme.label }}</span>
                                </button>
                            }
                        </div>
                    </section>

                    <p-button label="Restablecer marca" icon="pi pi-undo" severity="secondary" [outlined]="true" [disabled]="isBrandDefault()" (onClick)="resetToBrand()" styleClass="w-full" />
                }
            </div>
        </p-drawer>
    `
})
export class AppConfigurator implements OnInit {
    @Input() simple: boolean = false;

    layoutService: LayoutService = inject(LayoutService);
    private themeRuntime = inject(ThemeRuntimeService);

    /**
     * Personalización de marca (presets, color primario, superficie, sidebar,
     * marca, fondo) queda reservada a Super Administrador — el resto de
     * usuarios solo ve el interruptor claro/oscuro. El default de la app
     * (usuario sin preferencia guardada) es el preset "Nocturno"; esto solo
     * limita quién puede *cambiarlo* a otra cosa.
     */
    readonly allowThemes = environment.allowThemes;

    readonly brandColors = BRAND_PRIMARY_COLORS;
    readonly explorationColors = EXPLORATION_PRIMARY_COLORS;
    readonly surfaces = SURFACES;
    readonly sidebarSchemes = SIDEBAR_SCHEMES;
    readonly backgroundSchemes = BACKGROUND_SCHEMES;
    readonly logoVariants = LOGO_VARIANTS;
    readonly themePresets = THEME_PRESETS;

    themeOptions = [
        { name: 'Claro', value: false },
        { name: 'Oscuro', value: true }
    ];

    darkTheme = computed(() => this.layoutService.isDarkTheme());
    selectedPrimary = computed(() => this.themeRuntime.primary());
    selectedSurface = computed(() => this.themeRuntime.surface());
    selectedSidebarScheme = computed(() => this.themeRuntime.sidebarScheme());
    selectedBackgroundScheme = computed(() => this.themeRuntime.backgroundScheme());
    selectedLogoVariant = computed(() => this.themeRuntime.logoVariant());

    /**
     * Qué preset coincide con la configuración actual, o `null` si el usuario ha
     * tocado algo suelto. Se deriva por comparación en vez de guardarse: así no
     * hay un quinto valor en storage que pueda quedar desincronizado del resto
     * cuando se cambia un color a mano.
     *
     * `logoVariant` queda fuera de la comparación a propósito — los presets no
     * la tocan, es una preferencia independiente.
     */
    activePreset = computed<string | null>(() => {
        const match = this.themePresets.find(
            (p) =>
                p.primary === this.selectedPrimary() &&
                p.surface === this.selectedSurface() &&
                p.sidebarScheme === this.selectedSidebarScheme() &&
                p.backgroundScheme === this.selectedBackgroundScheme() &&
                p.dark === this.darkTheme()
        );
        return match?.id ?? null;
    });

    get visible() {
        return this.layoutService.layoutState().configSidebarVisible;
    }

    set visible(val: boolean) {
        this.layoutService.layoutState.update((state) => ({
            ...state,
            configSidebarVisible: val
        }));
    }

    ngOnInit() {
    }

    /**
     * El anillo de selección usa `outline-sym-blue-600` y no un color fijo: ese
     * token lo remapea ThemeRuntimeService, así que el indicador sigue al color
     * elegido en vez de quedarse azul sobre una paleta verde.
     */
    swatchClass(selected: boolean): string {
        const base = 'cursor-pointer w-6 h-6 rounded-full flex shrink-0 items-center justify-center p-0 border-0 outline-offset-2';
        return selected ? `${base} outline outline-2 outline-sym-blue-600` : `${base} outline-none`;
    }

    tileClass(selected: boolean): string {
        const base = 'cursor-pointer flex items-center gap-2 px-2 py-2 rounded-md text-sm text-left w-full transition-colors';
        return selected ? `${base} border border-sym-blue-600 bg-sym-blue-100 text-sym-blue-700` : `${base} border border-sym-border bg-transparent text-sym-gray-700 hover:bg-sym-surface`;
    }

    /**
     * Igual que tileClass pero en columna: la miniatura de la marca necesita
     * ancho completo para leerse, sobre todo el logotipo horizontal.
     */
    markTileClass(selected: boolean): string {
        const base = 'cursor-pointer flex flex-col items-center justify-center gap-1.5 px-2 py-2.5 rounded-md w-full transition-colors';
        return selected ? `${base} border border-sym-blue-600 bg-sym-blue-100 text-sym-blue-700` : `${base} border border-sym-border bg-transparent text-sym-gray-700 hover:bg-sym-surface`;
    }

    selectPrimary(id: string) {
        this.themeRuntime.setPrimary(id);
    }

    selectSurface(id: string) {
        this.themeRuntime.setSurface(id);
    }

    selectSidebarScheme(id: string) {
        this.themeRuntime.setSidebarScheme(id);
    }

    selectBackgroundScheme(id: string) {
        this.themeRuntime.setBackgroundScheme(id);
    }

    selectLogoVariant(id: string) {
        this.themeRuntime.setLogoVariant(id);
    }

    /**
     * El preset trae claro/oscuro, que vive en LayoutService, no en
     * ThemeRuntimeService. Se alterna SOLO si difiere: `toggleTheme()` es lo
     * único que expone LayoutService, así que llamarlo a ciegas invertiría el
     * tema al reaplicar un preset ya activo.
     */
    selectPreset(preset: ThemePresetOption) {
        this.themeRuntime.applyPreset(preset);
        if (preset.dark !== this.darkTheme()) {
            this.layoutService.toggleTheme();
        }
    }

    presetTileClass(selected: boolean): string {
        const base = 'cursor-pointer flex items-center gap-3 px-3 py-2 rounded-md w-full text-left transition-colors';
        return selected ? `${base} border border-sym-blue-600 bg-sym-blue-100 text-sym-blue-700` : `${base} border border-sym-border bg-transparent text-sym-gray-700 hover:bg-sym-surface`;
    }

    /**
     * La miniatura se elige contra el fondo del DRAWER (tema de la app), no
     * contra el cromo del sidebar: el tile vive en el drawer. Es distinto de
     * `AppSidebar.logo()`, que sí mira el cromo porque ahí el logo va dentro
     * del sidebar.
     */
    variantPreview(variant: LogoVariantOption): string {
        return this.darkTheme() && variant.previewDark ? variant.previewDark : variant.preview;
    }

    isBrandDefault(): boolean {
        return this.themeRuntime.isBrandDefault();
    }

    resetToBrand() {
        this.themeRuntime.reset();
    }

    toggleDarkMode() {
        this.layoutService.toggleTheme();
    }

    toggleConfigSidebar() {
        this.layoutService.layoutState.update((val) => ({ ...val, configSidebarVisible: !val.configSidebarVisible }));
    }
}
